import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import MainURL from '../MainURL';

// 공개 페이지를 열 때마다 방문 기록을 1건 보낸다(서버: routers/client/sitevisit/siteVisit.js).
// 관리자 콘솔(/admin)·교회 콘솔(/manage-*)·로그인 화면은 방문자 통계에서 뺀다.
const EXCLUDED_PREFIXES = ['/admin', '/manage-app', '/manage-roll', '/manage-finance', '/manage-ss', '/login'];

function isExcludedPath(path: string) {
  return EXCLUDED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

function randomId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// 저장소를 못 쓰는 환경(사생활 보호 모드 등)이면 이 페이지에서만 쓰는 ID로 대신한다.
const memoryIds: Record<string, string> = {};

function storedId(storage: () => Storage, key: string) {
  try {
    const store = storage();
    let id = store.getItem(key);
    if (!id) {
      id = randomId();
      store.setItem(key, id);
    }
    return id;
  } catch {
    memoryIds[key] = memoryIds[key] || randomId();
    return memoryIds[key];
  }
}

export default function SiteVisitTracker() {
  const location = useLocation();
  const lastKey = useRef('');
  const sentReferrer = useRef(false);

  useEffect(() => {
    const path = location.pathname;
    // 같은 화면이 다시 그려질 때(개발 모드 이중 실행 등) 중복 기록하지 않는다.
    if (lastKey.current === location.key) return;
    lastKey.current = location.key;
    if (isExcludedPath(path)) return;

    // 외부 유입 주소는 사이트에 들어온 첫 페이지에만 싣는다.
    const referrer = sentReferrer.current ? '' : document.referrer;
    sentReferrer.current = true;

    fetch(`${MainURL}/site-visits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        visitorId: storedId(() => window.localStorage, 'siteVisitorId'),
        sessionId: storedId(() => window.sessionStorage, 'siteVisitSessionId'),
        path,
        referrer,
      }),
    }).catch(() => {});
  }, [location.key, location.pathname]);

  return null;
}
